import {
  QUESTION_MODEL_STATISTICS_EXPORT_URL,
  QUESTION_MODEL_STATISTICS_REDUCE_URL,
  QUESTION_MODEL_STATISTICS_RESPONSE_URL,
  QUESTION_MODELS_API_URL,
  QUESTION_MODELS_FILTER_URL,
  QUESTION_MODELS_SEARCH_ALTERABLE_URL,
  QUESTION_MODELS_SEARCH_AVAILABLE_URL,
  QUESTION_MODELS_SEARCH_BY_DEFAULT_URL,
} from 'constants/ApiConstants';
import { PaginationType } from 'constants/Global';
import * as actionTypes from 'modules/questionModels/actionTypes';
import { QUESTION_MODEL_STATUS } from 'modules/questionModels/constants';

/**
 * @param {import('./types').FetchQuestionModelsParams} params
 * @param {boolean} alterable
 * @param {Object={}} meta
 * @return {import('./types').FetchQuestionModelsAction}
 */
export const fetchAlterableQuestions = (params, meta = {}) => {
  const fetchAction = fetchQuestions(params, meta);

  return {
    ...fetchAction,
    payload: {
      ...fetchAction.payload,
      request: {
        ...fetchAction.payload.request,
        url: QUESTION_MODELS_SEARCH_ALTERABLE_URL,
      },
    },
  };
};

/**
 * @param {import('./types').FetchQuestionModelsParams} params
 * @param {Object={}} meta
 * @return {import('./types').FetchQuestionModelsAction}
 */
const fetchQuestions = (params, meta = {}) => ({
  type: actionTypes.FETCH_QUESTION_MODELS,
  payload: {
    request: {
      url: QUESTION_MODELS_FILTER_URL,
      method: 'GET',
      params: {
        status: QUESTION_MODEL_STATUS.OPEN,
        projection: 'page',
        page: 0,
        size: 10,
        sort: 'name,asc',
        ...params,
      },
    },
  },
  meta,
});

/**
 * @return {import('./types').FetchQuestionModelsAction}
 */
export const fetchAvailableQuestions = (params, meta = {}) => ({
  type: actionTypes.FETCH_QUESTION_MODELS,
  payload: {
    request: {
      url: QUESTION_MODELS_SEARCH_AVAILABLE_URL,
      method: 'GET',
      params: {
        status: QUESTION_MODEL_STATUS.OPEN,
        projection: 'page',
        page: 0,
        size: 10,
        ...params,
      },
    },
  },
  meta,
});

/**
 * @return {import('./types').FetchQuestionModelsSuccessAction}
 */
export const fetchQuestionsSuccess = (questions, meta = {}) => ({
  type: actionTypes.FETCH_QUESTION_MODELS_SUCCESS,
  payload: questions,
  meta,
});

/**
 * @return {import('./types').FetchQuestionModelsFailureAction}
 */
export const fetchQuestionsFailure = (error, meta = {}) => ({
  type: actionTypes.FETCH_QUESTION_MODELS_FAILURE,
  payload: error,
  error: true,
  meta,
});

/**
 *
 * @param {string} questionId
 * @param {Object} meta
 * @returns {{payload: {request: {method: string, url: string}}, meta: {}, type: string}}
 */
export const fetchQuestionModel = (questionId, meta = {}) => ({
  type: actionTypes.FETCH_QUESTION_MODEL,
  payload: {
    request: {
      method: 'GET',
      url: `${QUESTION_MODELS_API_URL}/${questionId}`,
    },
  },
  meta,
});

/**
 *
 * @param {import('./types').QuestionModel} questionModel
 * @param {Object} meta
 * @returns {{payload, meta: {}, type: string}}
 */
export const fetchQuestionModelSuccess = (questionModel, meta = {}) => ({
  type: actionTypes.FETCH_QUESTION_MODEL_SUCCESS,
  payload: questionModel,
  meta,
});

/**
 *
 * @param {string} message
 * @param {Object} meta
 * @returns {{payload, meta: {}, type: string, error: boolean}}
 */
export const fetchQuestionModelFailure = (message, meta = {}) => ({
  type: actionTypes.FETCH_QUESTION_MODEL_FAILURE,
  payload: message,
  error: true,
  meta,
});

/**
 *
 * @param {string} questionId
 * @param {Object} meta
 * @returns {{payload, meta: {}, type: string}}
 */
export const selectQuestionModel = (questionId, meta = {}) => ({
  type: actionTypes.SELECT_QUESTION_MODEL,
  payload: questionId,
  meta,
});

/**
 *
 * @param {string} locale
 * @param {Object} meta
 * @returns {{payload, meta: {}, type: string}}
 */
export const setSelectedLocale = (locale, meta = {}) => ({
  type: actionTypes.SET_SELECTED_LOCALE,
  payload: locale,
  meta,
});

/**
 *
 * @param {import('./types').QuestionModel} questionModel
 * @returns {{payload: {request: {method: string, data, url: string}}, type: string}}
 */
export const updateQuestionModel = questionModel => ({
  type: actionTypes.UPDATE_QUESTION_MODEL,
  payload: {
    request: {
      url: `${QUESTION_MODELS_API_URL}/${questionModel.id}`,
      method: 'PUT',
      data: questionModel,
    },
  },
});

/**
 *
 * @param {import('./types').QuestionModel} questionModel
 * @param {Object} meta
 * @returns {{payload, meta: {}, type: string}}
 */
export const updateQuestionModelSuccess = (questionModel, meta = {}) => ({
  type: actionTypes.UPDATE_QUESTION_MODEL_SUCCESS,
  payload: questionModel,
  meta,
});

/**
 *
 * @param {string} message
 * @param {Object} meta
 * @returns {{payload, meta: {}, type: string, error: boolean}}
 */
export const updateQuestionModelFailure = (message, meta = {}) => ({
  type: actionTypes.UPDATE_QUESTION_MODEL_FAILURE,
  payload: message,
  error: true,
  meta,
});

/**
 *
 * @param {import('./types').QuestionModel} questionModel
 * @param {Object} meta
 * @returns {{payload: {request: {method: string, data, url: string}, meta: {}}, type: string}}
 */
export const createQuestionModel = (questionModel, meta = {}) => ({
  type: actionTypes.CREATE_QUESTION_MODEL,
  payload: {
    request: {
      url: QUESTION_MODELS_API_URL,
      method: 'POST',
      data: questionModel,
    },
    meta,
  },
});

/**
 *
 * @param {import('./types').QuestionModel} questionModel
 * @param {Object} meta
 * @returns {{payload, meta: {}, type: string}}
 */
export const createQuestionModelSuccess = (questionModel, meta = {}) => ({
  type: actionTypes.CREATE_QUESTION_MODEL_SUCCESS,
  payload: questionModel,
  meta,
});

/**
 *
 * @param {string} message
 * @param {Object} meta
 * @returns {{payload, meta: {}, type: string, error: boolean}}
 */
export const createQuestionModelFailure = (message, meta = {}) => ({
  type: actionTypes.CREATE_QUESTION_MODEL_FAILURE,
  payload: message,
  error: true,
  meta,
});

/**
 *
 * @param {string} id
 * @returns {import('./types').DeleteQuestionModelRequestAction}
 */
export const deleteQuestionModel = id => ({
  type: actionTypes.DELETE_QUESTION_MODEL,
  payload: {
    request: {
      url: `${QUESTION_MODELS_API_URL}/${id}`,
      method: 'DELETE',
    },
    id,
  },
});

/**
 *
 * @param {string} id
 * @returns {import('./types').DeleteQuestionModelSuccessAction}
 */
export const deleteQuestionModelSuccess = id => ({
  type: actionTypes.DELETE_QUESTION_MODEL_SUCCESS,
  payload: id,
});

/**
 *
 * @param {string} error
 * @returns  {import('./types').DeleteQuestionModelFailureAction}
 */
export const deleteQuestionModelFailure = error => ({
  type: actionTypes.DELETE_QUESTION_MODEL_FAILURE,
  payload: error,
  error: true,
});

/**
 * @param {{id: string, dealerId: string, groupId: string }} params
 * @param {Object} [meta={}]
 * @returns {import('./types').CloneQuestionModelRequestAction}
 */
export const cloneQuestionModel = ({ id, ...data }, meta = {}) => ({
  type: actionTypes.CLONE_QUESTION_MODEL,
  payload: {
    request: {
      url: `${QUESTION_MODELS_API_URL}/${id}/clone`,
      method: 'POST',
      data
    },
  },
  meta,
});

/**
 * @param {string} id
 * @param {Object} [meta={}]
 * @returns {import('./types').CloneQuestionModelSuccessAction}
 */
export const cloneQuestionModelSuccess = (id, meta = {}) => ({
  type: actionTypes.CLONE_QUESTION_MODEL_SUCCESS,
  payload: id,
  meta,
});

/**
 * @param error
 * @param {Object} [meta={}]
 * @returns {import('./types').CloneQuestionModelFailureAction}
 */
export const cloneQuestionModelFailure = (error, meta = {}) => ({
  type: actionTypes.CLONE_QUESTION_MODEL_FAILURE,
  payload: error,
  error: true,
  meta,
});

/**
 * @return {import('./types').FetchQuestionStatisticCount}
 */
export const fetchQuestionStatisticCount = (questionFormModelId, dealerId, groupId, period, filtersRequest) => ({
  type: actionTypes.QUESTION_MODEL_STATISTICS_COUNT,
  payload: {
    request: {
      url: QUESTION_MODEL_STATISTICS_REDUCE_URL,
      method: 'GET',
      params: {
        questionFormModelId,
        dealerId,
        groupId,
        response: filtersRequest,
        paginationType: PaginationType.SLICE,
        reducer: 'questionFormCount,func',
        from: period?.from,
        to: period?.to,
      },
    },
  },
});

/**
 *
 * @param {number} count
 * @returns {import('./types').FetchQuestionStatisticCountSuccessAction}
 */
export const fetchQuestionStatisticCountSuccess = count => ({
  type: actionTypes.QUESTION_MODEL_STATISTICS_COUNT_SUCCESS,
  payload: count,
});

/**
 *
 * @param {string} error
 * @returns {import('./types').FetchQuestionStatisticCountFailureAction}
 */
export const fetchQuestionStatisticCountFailure = error => ({
  type: actionTypes.QUESTION_MODEL_STATISTICS_COUNT_FAILURE,
  payload: error,
  error: true,
});

/** @return {import('./types').FetchQuestionStatisticAnswer} */
export const fetchAllQuestionStatisticsAnswer = params => ({
  type: actionTypes.QUESTION_MODEL_STATISTICS_ANSWERS,
  payload: {
    request: {
      url: QUESTION_MODEL_STATISTICS_RESPONSE_URL,
      method: 'GET',
      params,
    },
  },
  meta: { '@app-request': true },
});

/**
 *
 * @param {string} questionModelId
 * @returns {import('modules/global/generic.types').Action<string>}
 */
export const setSelectedQuestionModel = questionModelId => ({
  type: actionTypes.SET_QUESTION_MODEL_STATISTICS_ID,
  payload: questionModelId,
});

/**
 * @returns {import('modules/global/generic.types').Action<undefined>}
 */
export const clearQuestionModelStatistic = () => ({
  type: actionTypes.CLEAR_QUESTION_MODEL_STATISTIC,
});

/**
 * @param {string} questionFilterId
 * @param {string} choice
 * @returns {import('modules/global/generic.types').Action<{questionFilterId: string, choice: Choice}>}
 */
export const addFilterQuestionModelStatistic = (questionFilterId, choice) => ({
  type: actionTypes.ADD_FILTER_QUESTION_MODEL_STATISTIC,
  payload: {
    questionFilterId,
    choice,
  },
});

/**
 *
 * @param {string} questionFilterId
 * @param {string} choice
 * @returns {import('modules/global/generic.types').Action<string>}
 */
export const removeFilterQuestionModelStatistic = (questionFilterId, choice) => ({
  type: actionTypes.REMOVE_FILTER_QUESTION_MODEL_STATISTIC,
  payload: {
    questionFilterId,
    choice,
  },
});

/**
 *
 * @returns {import('modules/global/generic.types').Action<undefined>}
 */
export const removeAllFilterQuestionModelStatistic = () => ({
  type: actionTypes.REMOVE_ALL_FILTER_QUESTION_MODEL_STATISTIC,
});

/**
 *
 * @returns {import('modules/global/generic.types').Action<undefined>}
 */
export const openExportStatModal = () => ({
  type: actionTypes.OPEN_EXPORT_STATS_MODAL,
});

/**
 *
 * @returns {import('modules/global/generic.types').Action<undefined>}
 */
export const closeExportStatModal = () => ({
  type: actionTypes.CLOSE_EXPORT_STATS_MODAL,
});

/**
 * @param {import('./types').ExportQuestionModelProps} params
 * @return {import('./types').ExportQuestionStatistic}
 */
export const exportQuestionModel = (params) => ({
  type: actionTypes.EXPORT_QUESTION_MODEL_STATISTIC,
  payload: {
    request: {
      url: QUESTION_MODEL_STATISTICS_EXPORT_URL,
      method: 'POST',
      data: {
        ...params
      },
    },
  },
});

/**
 * @param {Object} meta
 * @returns {import('./types').FetchDefaultQuestionModelAction}
 */
export const fetchDefaultQuestionModel = (meta = {}) => ({
  type: actionTypes.FETCH_DEFAULT_QUESTION_MODEL,
  payload: {
    request: {
      method: 'GET',
      url: QUESTION_MODELS_SEARCH_BY_DEFAULT_URL,
    },
  },
  meta,
});

/**
 * @param {import('./types').FetchQuestionModelsResponse} payload
 * @param {Object} meta
 * @returns {import('./types').FetchDefaultQuestionModelAction}
 */
export const fetchDefaultQuestionModelSuccess = (payload, meta = {}) => ({
  type: actionTypes.FETCH_DEFAULT_QUESTION_MODEL_SUCCESS,
  payload,
  meta,
});

/**
 * @param {Error} error
 * @param {Object} meta
 * @returns {import('./types').FetchDefaultQuestionModelAction}
 */
export const fetchDefaultQuestionModelFailure = (error, meta = {}) => ({
  type: actionTypes.FETCH_DEFAULT_QUESTION_MODEL_FAILURE,
  payload: error,
  error: true,
  meta,
});

/**
 * @param {{dealerId: string, groupId: string, name: string}} payload
 * @param {Object} meta
 * @returns {import('./types').CreateFromDefaultQuestionModelAction}
 */
export const createDefaultQuestionModel = (payload, meta = {}) => ({
  type: actionTypes.CREATE_FROM_DEFAULT_QUESTION_MODEL,
  payload,
  meta,
});

/**
 * @param {Object={}} meta
 */
export const isReady = (meta = {}) => ({
  type: actionTypes.IS_DETAILS_READY,
  meta,
});
